import {
	getNumberPlateTypeIndex,
	VehicleModType,
	vehicleNumberPlateModIndexByType,
	VehicleNumberPlateType,
	VehicleWheelTireProfile,
} from '../../types';
import { VehicleTuningConfig, VehicleTuningItemConfig, VehicleTuningModConfig } from './VehicleTuningConfig';

export interface VehicleTuningResolutionState {
	vehicleModel: string | null;
	installedItemIds: readonly string[];
	installedKitId: string | null;
	temporaryItemIds: readonly string[];
	temporaryKitId: string | null;
	tiresProfile: VehicleWheelTireProfile;
	temporaryTiresProfile: VehicleWheelTireProfile | null;
	numberPlateType: VehicleNumberPlateType | null;
	isRegistered: boolean;
}

export interface ResolvedVehicleTuning {
	mods: Map<VehicleModType, ResolvedVehicleTuningMod>;
	ignoredItemIds: string[];
	ignoredKitIds: string[];
}

export interface ResolvedVehicleTuningMod {
	modIndex: number;
	wheelType: number;
	sourceModId: string | null;
}

export function isStockTuningId(id: string | null | undefined): boolean {
	return typeof id === 'string' && id.startsWith('stock_');
}

export function isTuningItemCompatibleWithKit(
	item: Pick<VehicleTuningItemConfig, 'id' | 'compatibleKitIds' | 'requiresKit'>,
	kitId: string | null,
): boolean {
	if (!kitId || isStockTuningId(kitId)) {
		return !item.requiresKit;
	}

	if (!item.compatibleKitIds?.length) {
		return !item.requiresKit;
	}

	return item.compatibleKitIds.includes(kitId);
}

export function getTuningItemModTypes(config: VehicleTuningConfig, item: VehicleTuningItemConfig): Set<VehicleModType> {
	return new Set(
		item.modIds
			.map((modId) => config.getModById(modId))
			.filter((mod): mod is VehicleTuningModConfig => mod !== null)
			.map((mod) => mod.modType),
	);
}

function isAvailableForVehicle(config: { vehicleModel: string | null }, vehicleModel: string | null): boolean {
	return config.vehicleModel === null || config.vehicleModel === vehicleModel;
}

function getKitItems(
	config: VehicleTuningConfig,
	kitId: string | null,
	vehicleModel: string | null,
): VehicleTuningItemConfig[] {
	if (!kitId) {
		return [];
	}

	const kit = config.getItemsKitById(kitId);
	if (!kit || !isAvailableForVehicle(kit, vehicleModel)) {
		return [];
	}

	return kit.itemIds
		.map((itemId) => config.getItemById(itemId))
		.filter((item): item is VehicleTuningItemConfig => item !== null && isAvailableForVehicle(item, vehicleModel));
}

export function resolveVehicleTuning(
	config: VehicleTuningConfig,
	state: VehicleTuningResolutionState,
): ResolvedVehicleTuning {
	const mods = new Map<VehicleModType, ResolvedVehicleTuningMod>();
	const sourceItems = new Map<VehicleModType, VehicleTuningItemConfig>();
	const ignoredItemIds: string[] = [];
	const ignoredKitIds: string[] = [];
	const temporaryKitIsActive = state.temporaryKitId !== null;
	const requestedKitId = temporaryKitIsActive
		? isStockTuningId(state.temporaryKitId)
			? null
			: state.temporaryKitId
		: isStockTuningId(state.installedKitId)
		? null
		: state.installedKitId;
	const requestedKit = requestedKitId ? config.getItemsKitById(requestedKitId) : null;
	const effectiveKitId =
		requestedKit && isAvailableForVehicle(requestedKit, state.vehicleModel) ? requestedKit.id : null;
	if (requestedKitId && !effectiveKitId) {
		ignoredKitIds.push(requestedKitId);
	}
	const temporaryCustomKitIsActive = temporaryKitIsActive && effectiveKitId !== null;
	const installedRequestedKitId = isStockTuningId(state.installedKitId) ? null : state.installedKitId;
	const installedKit = installedRequestedKitId ? config.getItemsKitById(installedRequestedKitId) : null;
	const installedEffectiveKitId =
		installedKit && isAvailableForVehicle(installedKit, state.vehicleModel) ? installedKit.id : null;
	const installedItemsKitId = temporaryCustomKitIsActive ? installedEffectiveKitId : effectiveKitId;
	const tireProfile = state.temporaryTiresProfile ?? state.tiresProfile;

	const applyItem = (item: VehicleTuningItemConfig): void => {
		const itemMods = item.modIds
			.map((modId) => config.getModById(modId))
			.filter(
				(mod): mod is VehicleTuningModConfig => mod !== null && isAvailableForVehicle(mod, state.vehicleModel),
			);
		if (isStockTuningId(item.id)) {
			for (const mod of itemMods) {
				mods.delete(mod.modType);
				sourceItems.delete(mod.modType);
			}
			return;
		}

		const hasProfileVariants = itemMods.some((mod) => mod.tireProfile !== null);
		const modsToApply = hasProfileVariants ? itemMods.filter((mod) => mod.tireProfile === tireProfile) : itemMods;

		for (const mod of modsToApply) {
			mods.set(mod.modType, {
				modIndex: mod.modIndex,
				wheelType: mod.wheelType,
				sourceModId: mod.id,
			});
			sourceItems.set(mod.modType, item);
		}
	};

	const applyInstalledItems = (): void => {
		for (const itemId of state.installedItemIds) {
			const item = config.getItemById(itemId);
			if (
				!item ||
				!isAvailableForVehicle(item, state.vehicleModel) ||
				!isTuningItemCompatibleWithKit(item, installedItemsKitId)
			) {
				ignoredItemIds.push(itemId);
				continue;
			}

			applyItem(item);
		}
	};
	const kitItems = getKitItems(config, effectiveKitId, state.vehicleModel);

	if (temporaryCustomKitIsActive) {
		// Preserve installed items outside the kit while letting the preview override matching mod groups.
		applyInstalledItems();
	}

	for (const item of kitItems) {
		applyItem(item);
	}

	if (!temporaryCustomKitIsActive) {
		applyInstalledItems();
	}

	for (const itemId of state.temporaryItemIds) {
		const item = config.getItemById(itemId);
		if (
			!item ||
			!isAvailableForVehicle(item, state.vehicleModel) ||
			!isTuningItemCompatibleWithKit(item, effectiveKitId)
		) {
			ignoredItemIds.push(itemId);
			continue;
		}

		applyItem(item);
	}

	const resolvePlate = (
		plateModType: VehicleModType.PLTHOLDER | VehicleModType.PLTVANITY,
		bumperModType: VehicleModType.BUMPER_F | VehicleModType.BUMPER_R,
	): void => {
		if (!state.isRegistered) {
			mods.set(plateModType, { modIndex: 255, wheelType: -1, sourceModId: null });
			return;
		}

		const numberPlateType = state.numberPlateType ?? VehicleNumberPlateType.Classic;
		const numberPlateTypeIndex = getNumberPlateTypeIndex(numberPlateType);
		const bumperItem = sourceItems.get(bumperModType);
		const plateModId =
			numberPlateTypeIndex === null ? null : bumperItem?.plateModIds?.[numberPlateTypeIndex] ?? null;
		const plateMod = plateModId ? config.getModById(plateModId) : null;

		mods.set(
			plateModType,
			plateMod
				? {
						modIndex: plateMod.modIndex,
						wheelType: plateMod.wheelType,
						sourceModId: plateMod.id,
				  }
				: {
						modIndex: vehicleNumberPlateModIndexByType[numberPlateType],
						wheelType: -1,
						sourceModId: null,
				  },
		);
	};

	resolvePlate(VehicleModType.PLTHOLDER, VehicleModType.BUMPER_F);
	resolvePlate(VehicleModType.PLTVANITY, VehicleModType.BUMPER_R);

	return { mods, ignoredItemIds, ignoredKitIds };
}
